import {
  type ApprovalRequest,
  ApprovalRequestSchema,
  type AutomationPolicy,
  AutomationPolicySchema,
  type ContextFact,
  ContextFactSchema,
  type ExecutionReceipt,
  ExecutionReceiptSchema,
  type ExecutionRun,
  ExecutionRunSchema,
  type PlanStep,
  PlanStepSchema,
  type PlanningRun,
  PlanningRunSchema,
  type ReviewReport,
  ReviewReportSchema,
  type ScheduleBlock,
  ScheduleBlockSchema,
  newId,
} from '@portfolio-pm/shared';
import * as Y from 'yjs';
import type { WithNow } from './doc.ts';
import {
  getApprovalRequestsMap,
  getAutomationPoliciesMap,
  getContextFactsMap,
  getExecutionReceiptsMap,
  getExecutionRunsMap,
  getPlanStepsMap,
  getPlanningRunsMap,
  getReviewReportsMap,
  getScheduleBlocksMap,
} from './doc.ts';

type CreateInput<T extends { id: string; createdAt: number; updatedAt?: number }> = Omit<
  T,
  'id' | 'createdAt' | 'updatedAt'
>;

function put<T extends { id: string }>(
  map: Y.Map<Y.Map<unknown>>,
  schema: { parse(value: unknown): T },
  value: T,
): T {
  const parsed = schema.parse(value);
  if (map.has(parsed.id)) throw new Error(`record ${parsed.id} already exists`);
  const inner = new Y.Map<unknown>();
  map.set(parsed.id, inner);
  write(inner, parsed);
  return parsed;
}

function write(inner: Y.Map<unknown>, value: { id: string }): void {
  const next = value as Record<string, unknown>;
  for (const key of [...inner.keys()]) {
    if (key !== 'id' && next[key] === undefined) inner.delete(key);
  }
  for (const [key, item] of Object.entries(next)) {
    if (key === 'id' || item === undefined) continue;
    inner.set(key, structuredClone(item));
  }
}

function read<T>(
  map: Y.Map<Y.Map<unknown>>,
  schema: { parse(value: unknown): T },
  id: string,
): T | undefined {
  const inner = map.get(id);
  return inner ? schema.parse({ id, ...inner.toJSON() }) : undefined;
}

function list<T>(map: Y.Map<Y.Map<unknown>>, schema: { parse(value: unknown): T }): T[] {
  return [...map.entries()].map(([id, inner]) => schema.parse({ id, ...inner.toJSON() }));
}

function patch<T extends { id: string; updatedAt?: number }>(
  map: Y.Map<Y.Map<unknown>>,
  schema: { parse(value: unknown): T },
  id: string,
  values: Partial<T>,
  now: number,
): T {
  const current = read(map, schema, id);
  if (!current) throw new Error(`record ${id} not found`);
  const next = schema.parse({ ...current, ...values, id, updatedAt: now });
  const inner = map.get(id);
  if (!inner) throw new Error(`record ${id} not found`);
  write(inner, next);
  return next;
}

export function createPlanningRun(
  doc: Y.Doc,
  input: CreateInput<PlanningRun>,
  options: WithNow = {},
): PlanningRun {
  const now = options.now ?? Date.now();
  return put(getPlanningRunsMap(doc), PlanningRunSchema, {
    ...input,
    id: newId(),
    createdAt: now,
    updatedAt: now,
  });
}

export function getPlanningRun(doc: Y.Doc, id: string): PlanningRun | undefined {
  return read(getPlanningRunsMap(doc), PlanningRunSchema, id);
}

export function listPlanningRuns(doc: Y.Doc): PlanningRun[] {
  return list(getPlanningRunsMap(doc), PlanningRunSchema).sort(
    (a, b) => b.createdAt - a.createdAt || a.id.localeCompare(b.id),
  );
}

export function updatePlanningRun(
  doc: Y.Doc,
  id: string,
  values: Partial<PlanningRun>,
  options: WithNow = {},
): PlanningRun {
  return patch(getPlanningRunsMap(doc), PlanningRunSchema, id, values, options.now ?? Date.now());
}

export function createPlanStep(
  doc: Y.Doc,
  input: CreateInput<PlanStep>,
  options: WithNow = {},
): PlanStep {
  if (!getPlanningRun(doc, input.planningRunId)) throw new Error('planning run not found');
  const now = options.now ?? Date.now();
  return put(getPlanStepsMap(doc), PlanStepSchema, {
    ...input,
    id: newId(),
    createdAt: now,
    updatedAt: now,
  });
}

export function listPlanSteps(doc: Y.Doc, planningRunId?: string): PlanStep[] {
  return list(getPlanStepsMap(doc), PlanStepSchema)
    .filter((step) => planningRunId === undefined || step.planningRunId === planningRunId)
    .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
}

export function createScheduleBlock(
  doc: Y.Doc,
  input: CreateInput<ScheduleBlock>,
  options: WithNow = {},
): ScheduleBlock {
  const now = options.now ?? Date.now();
  return put(getScheduleBlocksMap(doc), ScheduleBlockSchema, {
    ...input,
    id: newId(),
    createdAt: now,
    updatedAt: now,
  });
}

export function getScheduleBlock(doc: Y.Doc, id: string): ScheduleBlock | undefined {
  return read(getScheduleBlocksMap(doc), ScheduleBlockSchema, id);
}

export function listScheduleBlocks(doc: Y.Doc, planningRunId?: string): ScheduleBlock[] {
  return list(getScheduleBlocksMap(doc), ScheduleBlockSchema)
    .filter((block) => planningRunId === undefined || block.planningRunId === planningRunId)
    .sort((a, b) => a.startAt - b.startAt || a.id.localeCompare(b.id));
}

export function updateScheduleBlock(
  doc: Y.Doc,
  id: string,
  values: Partial<ScheduleBlock>,
  options: WithNow = {},
): ScheduleBlock {
  return patch(
    getScheduleBlocksMap(doc),
    ScheduleBlockSchema,
    id,
    values,
    options.now ?? Date.now(),
  );
}

export function createApprovalRequest(
  doc: Y.Doc,
  input: Omit<CreateInput<ApprovalRequest>, 'state' | 'requiredConfirmation' | 'expiresAt'> & {
    expiresAt?: number;
  },
  options: WithNow = {},
): ApprovalRequest {
  const now = options.now ?? Date.now();
  const id = newId();
  const destructive = input.risk === 'destructive';
  return put(getApprovalRequestsMap(doc), ApprovalRequestSchema, {
    ...input,
    id,
    state: 'pending',
    requiredConfirmation: `${destructive ? 'CONFIRM DESTRUCTIVE' : 'APPROVE'} ${id}`,
    expiresAt: input.expiresAt ?? now + 15 * 60_000,
    createdAt: now,
    updatedAt: now,
  });
}

export function listApprovalRequests(doc: Y.Doc): ApprovalRequest[] {
  return list(getApprovalRequestsMap(doc), ApprovalRequestSchema).sort(
    (a, b) => b.createdAt - a.createdAt || a.id.localeCompare(b.id),
  );
}

export function decideApprovalRequest(
  doc: Y.Doc,
  id: string,
  decision: 'approved' | 'rejected',
  actor: string,
  confirmation: string,
  options: WithNow = {},
): ApprovalRequest {
  const now = options.now ?? Date.now();
  const current = read(getApprovalRequestsMap(doc), ApprovalRequestSchema, id);
  if (!current) throw new Error('approval request not found');
  if (current.state !== 'pending') throw new Error(`approval request is ${current.state}`);
  if (current.expiresAt <= now) {
    patch(getApprovalRequestsMap(doc), ApprovalRequestSchema, id, { state: 'expired' }, now);
    throw new Error('approval request expired');
  }
  if (decision === 'approved' && confirmation !== current.requiredConfirmation) {
    throw new Error(`confirmation must equal ${current.requiredConfirmation}`);
  }
  return patch(
    getApprovalRequestsMap(doc),
    ApprovalRequestSchema,
    id,
    { state: decision, decidedBy: actor, decidedAt: now },
    now,
  );
}

export function consumeApprovalRequest(
  doc: Y.Doc,
  id: string,
  options: WithNow = {},
): ApprovalRequest {
  const now = options.now ?? Date.now();
  const current = read(getApprovalRequestsMap(doc), ApprovalRequestSchema, id);
  if (!current) throw new Error('approval request not found');
  if (current.state !== 'approved') throw new Error(`approval request is ${current.state}`);
  return patch(getApprovalRequestsMap(doc), ApprovalRequestSchema, id, { state: 'consumed' }, now);
}

export function createAutomationPolicy(
  doc: Y.Doc,
  input: CreateInput<AutomationPolicy>,
  options: WithNow = {},
): AutomationPolicy {
  const now = options.now ?? Date.now();
  return put(getAutomationPoliciesMap(doc), AutomationPolicySchema, {
    ...input,
    id: newId(),
    createdAt: now,
    updatedAt: now,
  });
}

export function listAutomationPolicies(doc: Y.Doc): AutomationPolicy[] {
  return list(getAutomationPoliciesMap(doc), AutomationPolicySchema);
}

export function createExecutionRun(
  doc: Y.Doc,
  input: Omit<CreateInput<ExecutionRun>, 'state' | 'attemptCount'>,
  options: WithNow = {},
): ExecutionRun {
  const duplicate = list(getExecutionRunsMap(doc), ExecutionRunSchema).find(
    (run) => run.idempotencyKey === input.idempotencyKey,
  );
  if (duplicate) return duplicate;
  const now = options.now ?? Date.now();
  return put(getExecutionRunsMap(doc), ExecutionRunSchema, {
    ...input,
    id: newId(),
    state: 'queued',
    attemptCount: 0,
    createdAt: now,
    updatedAt: now,
  });
}

export function listExecutionRuns(doc: Y.Doc): ExecutionRun[] {
  return list(getExecutionRunsMap(doc), ExecutionRunSchema).sort(
    (a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id),
  );
}

export function claimExecutionRun(
  doc: Y.Doc,
  id: string,
  owner: string,
  leaseMs = 5 * 60_000,
  options: WithNow = {},
): ExecutionRun {
  const now = options.now ?? Date.now();
  const run = read(getExecutionRunsMap(doc), ExecutionRunSchema, id);
  if (!run) throw new Error('execution run not found');
  if (run.state !== 'queued' && !(run.state === 'running' && (run.leaseExpiresAt ?? 0) <= now)) {
    throw new Error(`execution run is ${run.state}`);
  }
  return patch(
    getExecutionRunsMap(doc),
    ExecutionRunSchema,
    id,
    {
      state: 'running',
      leaseOwner: owner,
      leaseExpiresAt: now + leaseMs,
      attemptCount: run.attemptCount + 1,
    },
    now,
  );
}

export function recordExecutionReceipt(
  doc: Y.Doc,
  input: Omit<ExecutionReceipt, 'id' | 'createdAt'>,
  options: WithNow = {},
): ExecutionReceipt {
  const execution = read(getExecutionRunsMap(doc), ExecutionRunSchema, input.executionRunId);
  if (!execution) throw new Error('execution run not found');
  const now = options.now ?? Date.now();
  const receipt = put(getExecutionReceiptsMap(doc), ExecutionReceiptSchema, {
    ...input,
    id: newId(),
    createdAt: now,
  });
  patch(
    getExecutionRunsMap(doc),
    ExecutionRunSchema,
    execution.id,
    { state: 'succeeded', completedAt: now, leaseOwner: undefined, leaseExpiresAt: undefined },
    now,
  );
  return receipt;
}

export function listExecutionReceipts(doc: Y.Doc): ExecutionReceipt[] {
  return list(getExecutionReceiptsMap(doc), ExecutionReceiptSchema);
}

export function createContextFact(
  doc: Y.Doc,
  input: CreateInput<ContextFact>,
  options: WithNow = {},
): ContextFact {
  const now = options.now ?? Date.now();
  return put(getContextFactsMap(doc), ContextFactSchema, {
    ...input,
    id: newId(),
    createdAt: now,
    updatedAt: now,
  });
}

export function listContextFacts(doc: Y.Doc, includeInactive = false): ContextFact[] {
  const now = Date.now();
  return list(getContextFactsMap(doc), ContextFactSchema)
    .filter((fact) => includeInactive || (fact.state === 'confirmed' && fact.freshUntil > now))
    .sort((a, b) => a.scope.localeCompare(b.scope) || a.key.localeCompare(b.key));
}

export function updateContextFact(
  doc: Y.Doc,
  id: string,
  values: Partial<ContextFact>,
  options: WithNow = {},
): ContextFact {
  return patch(getContextFactsMap(doc), ContextFactSchema, id, values, options.now ?? Date.now());
}

export function deleteContextFact(doc: Y.Doc, id: string, options: WithNow = {}): void {
  const map = getContextFactsMap(doc);
  if (!map.has(id)) throw new Error('context fact not found');
  map.delete(id);
  doc.getMap<number>('deleted').set(id, options.now ?? Date.now());
}

export function createReviewReport(
  doc: Y.Doc,
  input: CreateInput<ReviewReport>,
  options: WithNow = {},
): ReviewReport {
  const now = options.now ?? Date.now();
  return put(getReviewReportsMap(doc), ReviewReportSchema, {
    ...input,
    id: newId(),
    createdAt: now,
    updatedAt: now,
  });
}

export function listReviewReports(doc: Y.Doc): ReviewReport[] {
  return list(getReviewReportsMap(doc), ReviewReportSchema).sort(
    (a, b) => b.periodEnd - a.periodEnd || a.id.localeCompare(b.id),
  );
}
